import type { PerformanceEventName } from '@ms/yammer-telemetry';
import {
  appLayoutRegionAttributeName,
  appLayoutRegions,
  appLayoutTemplateAttributeName,
} from '@ms/yammer-web-core-ui/dist/components/AppLayout';
import type { AppLayoutRegion } from '@ms/yammer-web-core-ui/dist/components/AppLayout';

type PageLoadPerformanceEventName = Extract<PerformanceEventName, 'page_load' | 'cached_first_page_load'>;
type LayoutMarkerFallback = 'shared' | 'outside-app-layout' | 'unknown';
type ResolvedAppLayoutRegion = AppLayoutRegion | LayoutMarkerFallback;

export type RouteClsPayload =
  | {
      readonly supported: false;
    }
  | {
      readonly supported: true;
      readonly sumOfLayoutShiftValues: number;
      readonly largestLayoutShiftValue: number;
      readonly count: number;
      readonly recentInputExcludedCount: number;
      readonly largestOffsetMs?: number;
      readonly region?: ResolvedAppLayoutRegion;
    };

interface RouteLayoutShiftMeasurementOptions {
  readonly eventName: PageLoadPerformanceEventName;
  readonly pageLoadId: number;
}

interface ActiveRouteLayoutShiftMeasurement {
  readonly isSupported: boolean;
  readonly sumOfLayoutShiftValues: number;
  readonly count: number;
  readonly largestLayoutShiftValue: number;
  readonly largestOffsetMs?: number;
  readonly largestAttribution?: RouteLayoutShiftAttribution;
  readonly recentInputExcludedCount: number;
  readonly startTime: number;
}

interface LayoutShiftPerformanceEntry extends PerformanceEntry {
  readonly hadRecentInput: boolean;
  readonly sources?: readonly LayoutShiftAttributionSource[];
  readonly value: number;
}

interface LayoutShiftAttributionSource {
  readonly node?: Node | null;
}

interface RouteLayoutShiftAttribution {
  readonly region: ResolvedAppLayoutRegion;
}

const activeMeasurements = new Map<string, ActiveRouteLayoutShiftMeasurement>();
const completedMeasurements = new Map<string, RouteClsPayload>();
const maxCompletedMeasurements = 50;

let layoutShiftObserver: PerformanceObserver | undefined;
let isLayoutShiftApiSupported: boolean | undefined;
let isVisibilityChangeListenerAttached = false;

const getMeasurementKey = ({ eventName, pageLoadId }: RouteLayoutShiftMeasurementOptions): string =>
  `${eventName}-${pageLoadId}`;

const isLayoutShiftPerformanceEntry = (entry: PerformanceEntry): entry is LayoutShiftPerformanceEntry =>
  'hadRecentInput' in entry &&
  typeof entry.hadRecentInput === 'boolean' &&
  'value' in entry &&
  typeof entry.value === 'number';

const isAppLayoutRegion = (value: string): value is AppLayoutRegion =>
  appLayoutRegions.some((region) => region === value);

const getSourceElement = (source: LayoutShiftAttributionSource): Element | undefined => {
  const { node } = source;
  if (!(node instanceof Node)) {
    return undefined;
  }

  return node instanceof Element ? node : (node.parentElement ?? undefined);
};

const getClosestAttribute = (element: Element, attributeName: string): string | undefined =>
  element.closest(`[${attributeName}]`)?.getAttribute(attributeName) ?? undefined;

const getLayoutRegion = (element: Element): AppLayoutRegion | 'outside-app-layout' | 'unknown' => {
  const value = getClosestAttribute(element, appLayoutRegionAttributeName);
  if (value && isAppLayoutRegion(value)) {
    return value;
  }

  return element.closest(`[${appLayoutTemplateAttributeName}]`) ? 'unknown' : 'outside-app-layout';
};

const resolveSharedValue = <Value extends string>(values: readonly Value[]): Value | 'shared' => {
  const firstValue = values[0];

  return values.every((value) => value === firstValue) ? firstValue : 'shared';
};

const resolveRouteLayoutShiftAttribution = (
  sources: readonly LayoutShiftAttributionSource[] | undefined
): RouteLayoutShiftAttribution => {
  const elements = sources?.map(getSourceElement).filter((element): element is Element => element !== undefined) ?? [];
  if (elements.length === 0) {
    return {
      region: 'unknown',
    };
  }

  return {
    region: resolveSharedValue(elements.map(getLayoutRegion)),
  };
};

const aggregateLayoutShiftEntries = (
  initialMeasurement: ActiveRouteLayoutShiftMeasurement,
  entries: readonly LayoutShiftPerformanceEntry[]
) => {
  let measurement = initialMeasurement;
  let largestEntry: LayoutShiftPerformanceEntry | undefined;

  entries.forEach((entry) => {
    if (entry.startTime < measurement.startTime) {
      return;
    }

    if (entry.hadRecentInput) {
      measurement = {
        ...measurement,
        recentInputExcludedCount: measurement.recentInputExcludedCount + 1,
      };

      return;
    }

    const isLargestEntry = entry.value > measurement.largestLayoutShiftValue;
    if (isLargestEntry) {
      largestEntry = entry;
    }

    measurement = {
      ...measurement,
      sumOfLayoutShiftValues: measurement.sumOfLayoutShiftValues + entry.value,
      count: measurement.count + 1,
      largestLayoutShiftValue: Math.max(measurement.largestLayoutShiftValue, entry.value),
      largestOffsetMs: isLargestEntry ? entry.startTime - measurement.startTime : measurement.largestOffsetMs,
    };
  });

  return { measurement, largestEntry };
};

const recordLayoutShiftEntries = (entries: PerformanceEntry[]) => {
  const layoutShiftEntries = entries.filter(isLayoutShiftPerformanceEntry);
  const attributions = new Map<LayoutShiftPerformanceEntry, RouteLayoutShiftAttribution>();

  activeMeasurements.forEach((initialMeasurement, key) => {
    const { measurement, largestEntry } = aggregateLayoutShiftEntries(initialMeasurement, layoutShiftEntries);
    if (!largestEntry) {
      activeMeasurements.set(key, measurement);

      return;
    }

    const attribution = attributions.get(largestEntry) ?? resolveRouteLayoutShiftAttribution(largestEntry.sources);
    attributions.set(largestEntry, attribution);
    activeMeasurements.set(key, { ...measurement, largestAttribution: attribution });
  });
};

const startLayoutShiftObserver = (): boolean => {
  if (layoutShiftObserver) {
    return true;
  }

  const supportedEntryTypes =
    typeof globalThis.PerformanceObserver === 'function'
      ? Reflect.get(globalThis.PerformanceObserver, 'supportedEntryTypes')
      : undefined;
  const doesPerformanceObserverSupportLayoutShift =
    Array.isArray(supportedEntryTypes) && supportedEntryTypes.includes('layout-shift');
  if (isLayoutShiftApiSupported === false || !doesPerformanceObserverSupportLayoutShift) {
    isLayoutShiftApiSupported = false;

    return false;
  }

  const observer = new globalThis.PerformanceObserver((entryList) => {
    recordLayoutShiftEntries(entryList.getEntries());
  });

  try {
    observer.observe({ type: 'layout-shift', buffered: false });

    layoutShiftObserver = observer;
    isLayoutShiftApiSupported = true;

    return true;
  } catch {
    observer.disconnect();

    isLayoutShiftApiSupported = false;

    return false;
  }
};

const stopLayoutShiftObserverWhenInactive = () => {
  if (activeMeasurements.size !== 0) {
    return;
  }

  layoutShiftObserver?.disconnect();
  layoutShiftObserver = undefined;

  document.removeEventListener('visibilitychange', finalizeMeasurementsWhenDocumentIsHidden);
  isVisibilityChangeListenerAttached = false;
};

const addVisibilityChangeListener = () => {
  if (isVisibilityChangeListenerAttached) {
    return;
  }

  document.addEventListener('visibilitychange', finalizeMeasurementsWhenDocumentIsHidden);
  isVisibilityChangeListenerAttached = true;
};

const retainCompletedMeasurement = (key: string, payload: RouteClsPayload) => {
  completedMeasurements.set(key, payload);

  const excessCompletedMeasurements = completedMeasurements.size - maxCompletedMeasurements;
  if (excessCompletedMeasurements <= 0) {
    return;
  }

  Array.from(completedMeasurements.keys())
    .slice(0, excessCompletedMeasurements)
    .forEach((oldestKey) => completedMeasurements.delete(oldestKey));
};

const getMeasurementPayload = (measurement: ActiveRouteLayoutShiftMeasurement): RouteClsPayload => {
  if (!measurement.isSupported) {
    return { supported: false };
  }

  const payload: RouteClsPayload = {
    supported: true,
    sumOfLayoutShiftValues: measurement.sumOfLayoutShiftValues,
    largestLayoutShiftValue: measurement.largestLayoutShiftValue,
    count: measurement.count,
    recentInputExcludedCount: measurement.recentInputExcludedCount,
  };

  if (measurement.largestOffsetMs === undefined || measurement.largestAttribution === undefined) {
    return payload;
  }

  return {
    ...payload,
    largestOffsetMs: measurement.largestOffsetMs,
    region: measurement.largestAttribution.region,
  };
};

const finalizeMeasurement = (key: string): RouteClsPayload | undefined => {
  recordLayoutShiftEntries(layoutShiftObserver?.takeRecords() ?? []);

  const measurement = activeMeasurements.get(key);
  if (!measurement) {
    return completedMeasurements.get(key);
  }

  const payload = getMeasurementPayload(measurement);
  activeMeasurements.delete(key);
  retainCompletedMeasurement(key, payload);
  stopLayoutShiftObserverWhenInactive();

  return payload;
};

const finalizeMeasurementsWhenDocumentIsHidden = () => {
  if (document.visibilityState !== 'hidden') {
    return;
  }

  Array.from(activeMeasurements.keys()).forEach(finalizeMeasurement);
};

const finalizePreviousPageLoadMeasurements = () => {
  Array.from(activeMeasurements.keys())
    .filter((key) => key.startsWith('page_load-'))
    .forEach(finalizeMeasurement);
};

export const startRouteLayoutShiftMeasurement = (options: RouteLayoutShiftMeasurementOptions): void => {
  const key = getMeasurementKey(options);
  if (document.visibilityState === 'hidden') {
    completedMeasurements.delete(key);

    return;
  }

  if (activeMeasurements.has(key)) {
    return;
  }

  if (options.eventName === 'page_load') {
    finalizePreviousPageLoadMeasurements();
  }

  completedMeasurements.delete(key);
  const isSupported = startLayoutShiftObserver();
  recordLayoutShiftEntries(layoutShiftObserver?.takeRecords() ?? []);
  activeMeasurements.set(key, {
    isSupported,
    sumOfLayoutShiftValues: 0,
    count: 0,
    largestLayoutShiftValue: 0,
    recentInputExcludedCount: 0,
    startTime: performance.now(),
  });
  addVisibilityChangeListener();
};

export const finishRouteLayoutShiftMeasurement = (
  options: RouteLayoutShiftMeasurementOptions
): RouteClsPayload | undefined => finalizeMeasurement(getMeasurementKey(options));

export const debugResetRouteLayoutShiftMeasurements = (): void => {
  activeMeasurements.clear();
  completedMeasurements.clear();
  layoutShiftObserver?.disconnect();
  layoutShiftObserver = undefined;
  isLayoutShiftApiSupported = undefined;

  if (isVisibilityChangeListenerAttached) {
    document.removeEventListener('visibilitychange', finalizeMeasurementsWhenDocumentIsHidden);
    isVisibilityChangeListenerAttached = false;
  }
};
